import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Estado de autenticação.
 *
 * O **refresh token** é persistido; o **access token** fica só em memória.
 *
 * Isso é deliberado e é o meio-termo honesto: guardar o access token em
 * `localStorage` o expõe a qualquer XSS pelos 15 minutos de validade, e não
 * guardar nada obrigaria login a cada aba nova. O refresh é rotativo e
 * revogável — se vazar e for usado, o servidor detecta o reuso e derruba a
 * família inteira. O access, não: ele vale até expirar.
 *
 * A alternativa correta de verdade é cookie `httpOnly` + `SameSite=Strict`,
 * que entra quando o front e a API estiverem sob o mesmo domínio em produção.
 */

export interface AuthUser {
  id: string;
  name: string;
  email: string;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  mfaRequired: boolean;
  status: 'anonymous' | 'authenticating' | 'authenticated' | 'mfa-pending';

  login: (email: string, password: string, tenantSlug?: string) => Promise<void>;
  verifyMfa: (code: string) => Promise<void>;
  loginWithAzureResult: (result: AuthResponse) => void;
  refresh: () => Promise<boolean>;
  logout: () => Promise<void>;
}

interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  mfaRequired: boolean;
  user: AuthUser;
}

const BASE = '/api/v1';

/**
 * Renovação em andamento, compartilhada por todo o app.
 *
 * O refresh é **rotativo**: cada uso invalida o anterior. Duas renovações
 * concorrentes com o mesmo token fazem o servidor detectar reuso e revogar a
 * sessão inteira — comportamento correto dele, e desastroso para quem só abriu
 * duas abas. Acontece de verdade: o `StrictMode` do React dispara o efeito de
 * montagem duas vezes, e um lote de requisições após o token expirar faz o
 * mesmo.
 *
 * A guarda mora aqui, no store, e não no cliente HTTP: `refresh()` é chamado
 * dos dois lugares.
 */
let inFlightRefresh: Promise<boolean> | null = null;

async function callAuth(path: string, body: unknown, token?: string | null): Promise<AuthResponse> {
  const response = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as Record<string, unknown>) : {};

  if (!response.ok) {
    throw new Error((payload['message'] as string) ?? 'Não foi possível entrar.');
  }
  return payload as unknown as AuthResponse;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, getState) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      mfaRequired: false,
      status: 'anonymous',

      async login(email, password, tenantSlug) {
        set({ status: 'authenticating' });
        try {
          const result = await callAuth('/auth/login', {
            email,
            password,
            ...(tenantSlug ? { tenantSlug } : {}),
          });
          set({
            accessToken: result.accessToken,
            refreshToken: result.refreshToken,
            user: result.user,
            mfaRequired: result.mfaRequired,
            status: result.mfaRequired ? 'mfa-pending' : 'authenticated',
          });
        } catch (error) {
          set({ status: 'anonymous', accessToken: null, refreshToken: null, user: null });
          throw error;
        }
      },

      async verifyMfa(code) {
        const result = await callAuth('/auth/mfa', { code }, getState().accessToken);
        set({
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
          user: result.user,
          mfaRequired: false,
          status: 'authenticated',
        });
      },

      loginWithAzureResult(result) {
        set({
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
          user: result.user,
          mfaRequired: false,
          status: 'authenticated',
        });
      },

      async refresh() {
        // Quem chegar durante uma renovação em curso espera a mesma promessa.
        inFlightRefresh ??= (async () => {
          const token = getState().refreshToken;
          if (!token) return false;
          try {
            const result = await callAuth('/auth/refresh', { refreshToken: token });
            set({
              accessToken: result.accessToken,
              refreshToken: result.refreshToken,
              user: result.user,
              status: 'authenticated',
            });
            return true;
          } catch {
            // Recusa significa sessão encerrada — inclusive quando foi o
            // servidor que a revogou por detectar reuso de token.
            set({ accessToken: null, refreshToken: null, user: null, status: 'anonymous' });
            return false;
          } finally {
            inFlightRefresh = null;
          }
        })();
        return inFlightRefresh;
      },

      async logout() {
        const token = getState().refreshToken;
        set({ accessToken: null, refreshToken: null, user: null, status: 'anonymous' });
        if (!token) return;
        await fetch(`${BASE}/auth/logout`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ refreshToken: token }),
        }).catch(() => {
          // Sair localmente já aconteceu; falha de rede aqui não deve travar
          // a interface nem manter o usuário "dentro".
        });
      },
    }),
    {
      name: 'central.auth',
      // Só o refresh sobrevive ao recarregamento — ver a nota no topo.
      partialize: (state) => ({ refreshToken: state.refreshToken, user: state.user }),
    },
  ),
);
